import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TrendUp, TrendDown, ChartBar, ListNumbers } from "@phosphor-icons/react";

/**
 * PickupWidget — shows total pickup (sudah_termuat + diverifikasi) with
 * period selector (month/week/day), district filter, chart/number toggle,
 * and vs previous / vs YoY comparison.
 */
export default function PickupWidget() {
  const [period, setPeriod] = useState("month");
  const [districtId, setDistrictId] = useState("__all__");
  const [districts, setDistricts] = useState([]);
  const [data, setData] = useState(null);
  const [view, setView] = useState("chart"); // chart | number
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/districts", { params: { aktif: true } })
      .then(({ data }) => setDistricts(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { period };
    if (districtId !== "__all__") params.district_id = districtId;
    api.get("/analytics/pickup", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, districtId]);

  const latest = data?.latest || { current: 0, prev_period: 0, prev_year: 0 };
  const cmp = data?.comparison || { vs_prev_period_pct: null, vs_prev_year_pct: null };
  const maxVal = Math.max(1, ...(data?.series || []).map((s) => Math.max(s.current, s.prev_period, s.prev_year)));

  return (
    <div className="border border-border bg-card p-5" data-testid="pickup-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="overline">Total Pengambilan (Sudah Termuat)</div>
          <div className="text-xs text-muted-foreground mt-1">
            Menampilkan periode terakhir + perbandingan
          </div>
        </div>
        <div className="flex gap-1">
          <Button
            size="sm" variant={view === "chart" ? "default" : "outline"}
            className="rounded-none" onClick={() => setView("chart")}
            data-testid="pickup-view-chart"
          >
            <ChartBar size={14} className="mr-1" /> Grafik
          </Button>
          <Button
            size="sm" variant={view === "number" ? "default" : "outline"}
            className="rounded-none" onClick={() => setView("number")}
            data-testid="pickup-view-number"
          >
            <ListNumbers size={14} className="mr-1" /> Angka
          </Button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="rounded-none h-9 w-32" data-testid="pickup-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Harian</SelectItem>
            <SelectItem value="week">Mingguan</SelectItem>
            <SelectItem value="month">Bulanan</SelectItem>
          </SelectContent>
        </Select>
        <Select value={districtId} onValueChange={setDistrictId}>
          <SelectTrigger className="rounded-none h-9 flex-1 min-w-[10rem]" data-testid="pickup-district">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Semua Distrik</SelectItem>
            {districts.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.kode} · {d.nama}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="p-6 text-center overline">Memuat...</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <Kpi label="Periode Terakhir" value={latest.current} count={latest.current_count} />
            <Kpi
              label="Vs. Sebelumnya"
              value={latest.prev_period}
              count={latest.prev_period_count}
              pct={cmp.vs_prev_period_pct}
              testId="cmp-prev"
            />
            <Kpi
              label="Vs. Tahun Lalu"
              value={latest.prev_year}
              count={latest.prev_year_count}
              pct={cmp.vs_prev_year_pct}
              testId="cmp-yoy"
            />
          </div>

          {/* Chart / Numbers view */}
          {view === "chart" ? (
            <div className="space-y-2" data-testid="pickup-chart">
              {(data?.series || []).map((s, i) => (
                <div key={`bar-${s.label || i}`} className="flex items-center gap-2 text-xs">
                  <div className="w-10 font-mono">{s.label}</div>
                  <div className="flex-1 relative h-6 bg-secondary/40">
                    <div className="absolute inset-y-0 left-0 bg-primary/80" style={{ width: `${(s.current / maxVal) * 100}%` }} />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-2 font-bold text-[10px] text-foreground">
                      {s.current}
                    </div>
                  </div>
                  <div className="w-16 text-right font-mono text-[10px] text-muted-foreground">
                    {s.current_count || 0} SPB
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="pickup-table">
              <thead className="border-b border-border">
                <tr className="text-left overline">
                  <th className="py-2">Periode</th>
                  <th className="py-2 text-right">Qty</th>
                  <th className="py-2 text-right">SPB</th>
                </tr>
              </thead>
              <tbody>
                {(data?.series || []).map((s, i) => (
                  <tr key={`row-${s.label || i}`} className="border-b border-border/40">
                    <td className="py-2 font-mono">{s.label}</td>
                    <td className="py-2 font-mono text-right font-bold">{s.current}</td>
                    <td className="py-2 font-mono text-right text-muted-foreground">{s.current_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, count, pct, testId }) {
  const up = pct != null && pct > 0;
  const down = pct != null && pct < 0;
  return (
    <div className="border border-border p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-xl font-black font-mono mt-1">{value}</div>
      {count != null && <div className="text-[10px] text-muted-foreground">{count} SPB</div>}
      {pct != null && (
        <div className={`text-xs mt-1 font-bold flex items-center gap-1 ${up ? "text-emerald-600" : down ? "text-red-600" : "text-muted-foreground"}`}>
          {up && <TrendUp size={12} weight="bold" />}
          {down && <TrendDown size={12} weight="bold" />}
          {pct > 0 ? "+" : ""}{pct}%
        </div>
      )}
    </div>
  );
}
